package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import com.kaihuoy.components.common.*
import com.kaihuoy.shared.data.PainekoeRecord
import com.kaihuoy.web.FirebaseService
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

@Composable
fun PainekoeAdminTab() {
    val isMobile = window.innerWidth <= 900
    val scope = rememberCoroutineScope()
    var records by remember { mutableStateOf<List<PainekoeRecord>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var selectedRecord by remember { mutableStateOf<PainekoeRecord?>(null) }
    var filterUser by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        try {
            FirebaseService.database.reference("painekokeet").valueEvents.collect { snapshot ->
                records = snapshot.children.mapNotNull { child ->
                    try {
                        val r = child.value<PainekoeRecord>()
                        if (r.id.isBlank()) r.copy(id = child.key ?: "") else r
                    } catch (e: Exception) {
                        console.error("Virhe painekoe-tietueen luvussa: ${child.key}", e)
                        null
                    }
                }.sortedByDescending { it.timestamp }
                selectedRecord?.let { cur -> selectedRecord = records.find { it.id == cur.id } }
                isLoading = false
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            console.error("Painekokeiden haku epäonnistui", e)
            isLoading = false
        }
    }

    if (isLoading) {
        P { Text("Ladataan...") }
        return
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(if (isMobile) FlexDirection.Column else FlexDirection.Row)
            gap(32.px)
            alignItems(AlignItems.FlexStart)
        }
    }) {
        // Vasen: lista
        Div(attrs = {
            classes("no-print")
            style {
                flex("1")
                width(100.percent)
                if (!isMobile && selectedRecord != null) maxWidth(400.px)
                property("transition", "all 0.3s ease")
            }
        }) {
            val userOptions = remember(records) {
                listOf(DropdownOption("", "Kaikki tekijät")) +
                    records.mapNotNull { it.userName.takeIf { n -> n.isNotBlank() } }
                        .distinct().sorted().map { DropdownOption(it, it) }
            }
            val filtered = remember(records, filterUser) {
                if (filterUser.isEmpty()) records else records.filter { it.userName == filterUser }
            }

            StyledDropdown(
                value = filterUser,
                options = userOptions,
                placeholder = "Kaikki tekijät",
                label = "Tekijä",
                onSelect = { filterUser = it; selectedRecord = null }
            )
            Div(attrs = { style { marginBottom(12.px) } }) {
                Span(attrs = { style { fontSize(0.82.em); color(KaihuTheme.TextSecondary) } }) {
                    Text("${filtered.size} / ${records.size} koeponnistusta")
                }
            }

            if (filtered.isEmpty()) {
                P(attrs = { style { color(KaihuTheme.TextSecondary) } }) {
                    Text("Ei koeponnistuspöytäkirjoja.")
                }
            } else {
                filtered.forEach { record ->
                    PainekoeListItem(
                        record = record,
                        isSelected = selectedRecord?.id == record.id,
                        onClick = { selectedRecord = record },
                        onDelete = {
                            AppFeedback.confirm(
                                "Poistetaanko pöytäkirja?",
                                "Poistetaan ${record.userName}:n pöytäkirja kohteesta \"${record.tilaaja.ifBlank { record.katuosoite }}\"?",
                                "Poista",
                                confirmColor = KaihuTheme.Danger
                            ) {
                                scope.launch {
                                    try {
                                        FirebaseService.database.reference("painekokeet").child(record.id).removeValue()
                                        if (selectedRecord?.id == record.id) selectedRecord = null
                                        AppFeedback.showSuccess("Poistettu")
                                    } catch (e: Exception) {
                                        AppFeedback.showError("Poisto epäonnistui")
                                    }
                                }
                            }
                        }
                    )
                }
            }
        }

        // Oikea: yksityiskohdat
        if (selectedRecord != null) {
            Div(attrs = {
                classes("print-container")
                style {
                    flex("2")
                    width(100.percent)
                    if (!isMobile) {
                        property("position", "sticky")
                        top(20.px)
                    }
                }
            }) {
                PainekoeDetails(selectedRecord!!)
            }
        } else {
            Div(attrs = {
                style {
                    flex("2"); width(100.percent)
                    display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    padding(if (isMobile) 28.px else 64.px)
                    backgroundColor(KaihuTheme.Divider); borderRadius(24.px)
                    border(1.px, LineStyle.Dashed, KaihuTheme.Border)
                    color(KaihuTheme.TextLight)
                }
            }) {
                Span(attrs = { style { fontSize(3.em); marginBottom(16.px) } }) { TIcon("file", 3.em) }
                P { Text("Valitse pöytäkirja listasta nähdäksesi tiedot") }
            }
        }
    }
}

@Composable
private fun PainekoeListItem(
    record: PainekoeRecord,
    isSelected: Boolean,
    onClick: () -> Unit,
    onDelete: () -> Unit
) {
    val dateStr = if (record.timestamp > 0)
        Date(record.timestamp.toDouble()).toLocaleDateString("fi-FI") else record.asennusPvm.ifBlank { "-" }

    Div(attrs = {
        onClick { onClick() }
        style {
            padding(14.px, 18.px); marginBottom(8.px); borderRadius(12.px); cursor("pointer")
            backgroundColor(if (isSelected) Color("#1E3A5F") else KaihuTheme.Surface)
            property("border", "1px solid ${if (isSelected) "#2563EB" else KaihuTheme.Border}")
            property("box-shadow", if (isSelected) KaihuTheme.ShadowMd else KaihuTheme.ShadowSm)
            property("transition", "all 0.15s ease")
        }
    }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart); gap(8.px)
            }
        }) {
            Div(attrs = { style { flex(1) } }) {
                Div(attrs = { style { fontWeight("700"); color(KaihuTheme.TextPrimary); marginBottom(4.px) } }) {
                    Text(record.tilaaja.ifBlank { record.katuosoite.ifBlank { "Painekoe" } })
                }
                Div(attrs = { style { fontSize(0.88.em); color(KaihuTheme.TextSecondary); marginBottom(4.px) } }) {
                    Text(record.userName.ifBlank { "Tuntematon" })
                }
                if (record.katuosoite.isNotBlank()) {
                    Div(attrs = { style { fontSize(0.82.em); color(KaihuTheme.TextLight) } }) {
                        Text(record.katuosoite)
                    }
                }
                Span(attrs = { style { fontSize(0.8.em); color(KaihuTheme.TextLight) } }) { Text(dateStr) }
            }
            DelButton(text = "", icon = "trash", onClick = { onDelete() }, isDanger = true)
        }
    }
}

@Composable
private fun PainekoeDetails(record: PainekoeRecord) {
    val dateStr = if (record.timestamp > 0)
        Date(record.timestamp.toDouble()).toLocaleString("fi-FI") else record.asennusPvm.ifBlank { "-" }

    Div(attrs = {
        id("painekoe-print-${record.id}")
        style {
            property("background", KaihuTheme.CardGradient); borderRadius(16.px); padding(24.px)
            property("border", "1px solid ${KaihuTheme.Border}")
            property("box-shadow", KaihuTheme.ShadowSm)
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(20.px)
        }
    }) {
        // Otsikko + Tulosta
        Div(attrs = {
            style {
                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
            }
        }) {
            Div {
                H3(attrs = { style { margin(0.px, 0.px, 4.px, 0.px); color(KaihuTheme.TextPrimary) } }) {
                    Text("Koeponnistuspöytäkirja")
                }
                Div(attrs = { style { fontSize(0.85.em); color(KaihuTheme.TextLight) } }) { Text(dateStr) }
            }
            Button(attrs = {
                classes("no-print")
                onClick { printElementAsNewWindow("painekoe-print-${record.id}", "Koeponnistuspöytäkirja — ${record.tilaaja.ifBlank { dateStr }}") }
                style {
                    padding(8.px, 16.px); borderRadius(10.px); border(0.px)
                    backgroundColor(KaihuTheme.Primary); color(Color.white)
                    cursor("pointer"); fontWeight("600"); fontSize(0.88.em)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px)
                }
            }) { TIcon("printer"); Text(" Tulosta PDF") }
        }

        // Perustiedot
        DetailSection("Perustiedot") {
            DetailGrid {
                DetailCell("Koeponnistuksen tekijä", record.userName)
                DetailCell("Asennus pvm", record.asennusPvm)
            }
        }

        // Kohteen tiedot
        DetailSection("1. Kohteen tiedot") {
            if (record.rakennusTiedot.isNotBlank())
                DetailCell("Rakennuksen / järjestelmän tiedot", record.rakennusTiedot)
            DetailGrid {
                DetailCell("Tilaaja / Rakentaja", record.tilaaja)
                DetailCell("Puhelin / sähköposti", record.puhelin)
                DetailCell("Katuosoite", record.katuosoite)
                DetailCell("Postinumero / paikkakunta", record.postinumero)
            }
            if (record.jarjestelmanKuvaus.isNotBlank())
                DetailCell("Koeponnistettavan järjestelmän kuvaus", record.jarjestelmanKuvaus)
        }

        // Koeponnistustiedot
        DetailSection("2. Koeponnistustiedot") {
            DetailGrid {
                DetailCell("Lähtöpaine (bar)", record.lahtopaine)
                DetailCell("Nostetaan paine (bar)", record.nostetaanPaine)
                DetailCell("Veden määrä alussa (l)", record.vedenMaaraAlussa)
                DetailCell("Pidetään paine (min)", record.pidetaanPaine)
            }

            if (record.paineenNostot.isNotEmpty()) {
                Div(attrs = {
                    style {
                        marginTop(8.px)
                        property("border", "1px solid ${KaihuTheme.Border}")
                        borderRadius(10.px)
                        overflow("hidden")
                    }
                }) {
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "50px 1fr 1fr")
                            padding(8.px, 14.px)
                            backgroundColor(KaihuTheme.Surface)
                            property("border-bottom", "1px solid ${KaihuTheme.Border}")
                        }
                    }) {
                        listOf("#", "Kellonaika", "Veden määrä (l)").forEach { h ->
                            Span(attrs = {
                                style {
                                    fontSize(0.75.em); fontWeight("700"); color(KaihuTheme.TextLight)
                                    property("text-transform", "uppercase")
                                }
                            }) { Text(h) }
                        }
                    }
                    record.paineenNostot.forEachIndexed { idx, nosto ->
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "50px 1fr 1fr")
                                padding(10.px, 14.px)
                                alignItems(AlignItems.Center)
                                if (idx % 2 != 0) backgroundColor(KaihuTheme.CardBackground)
                                property("border-bottom", "1px solid ${KaihuTheme.Divider}")
                            }
                        }) {
                            Span(attrs = { style { color(KaihuTheme.TextSecondary); fontSize(0.85.em) } }) { Text("${idx + 1}.") }
                            Span(attrs = { style { color(KaihuTheme.TextPrimary); fontSize(0.88.em) } }) { Text(nosto.aika) }
                            Span(attrs = { style { color(KaihuTheme.TextPrimary); fontSize(0.88.em) } }) { Text(nosto.vedenMaara) }
                        }
                    }
                }
            }

            DetailGrid {
                DetailCell("Lasketaan paine (bar)", record.lasketaanPaine)
                DetailCell("Veden määrä ulos (l)", record.vedenMaaraUlos)
            }
            if (record.loppullinenVedenMaaraUlos.isNotBlank())
                DetailCell("Loppullinen veden määrä ulos (l)", record.loppullinenVedenMaaraUlos)
        }

        if (record.osio3.isNotBlank()) {
            DetailSection("3. Lisätiedot") {
                DetailCell("Lisätiedot / havainnot", record.osio3)
            }
        }

        if (record.huomautuksia.isNotBlank()) {
            DetailSection("Huomautuksia") {
                DetailCell("Huomautuksia", record.huomautuksia)
            }
        }

        // Allekirjoitukset
        DetailSection("Allekirjoitukset") {
            DetailGrid {
                DetailCell("Kokeen suorittaja", record.kokeenSuorittaja)
                DetailCell("Tilaajan edustaja", record.tilaajanEdustaja)
            }
        }
    }
}

@Composable
private fun DetailSection(title: String, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            backgroundColor(KaihuTheme.Background); borderRadius(12.px); padding(16.px)
            property("border", "1px solid ${KaihuTheme.Divider}")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px)
        }
    }) {
        Div(attrs = {
            style {
                fontSize(0.8.em); fontWeight("700"); color(KaihuTheme.TextSecondary)
                property("text-transform", "uppercase"); property("letter-spacing", "0.06em")
                marginBottom(4.px)
            }
        }) { Text(title) }
        content()
    }
}

@Composable
private fun DetailGrid(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(200px, 1fr))")
            gap(10.px)
        }
    }) {
        content()
    }
}

@Composable
private fun DetailCell(label: String, value: String) {
    if (value.isBlank()) return
    Div {
        Div(attrs = {
            style {
                fontSize(0.75.em); color(KaihuTheme.TextSecondary); fontWeight("600")
                property("text-transform", "uppercase"); property("letter-spacing", "0.04em"); marginBottom(2.px)
            }
        }) { Text(label) }
        Div(attrs = { style { fontSize(0.9.em); color(KaihuTheme.TextPrimary); fontWeight("500") } }) { Text(value) }
    }
}
